import React, { useRef, useEffect, useState, useCallback } from 'react';
import { IconButton, Badge, Tooltip } from "@mui/material";
import AddReactionIcon from '@mui/icons-material/AddReaction';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import Groups2Icon from '@mui/icons-material/Groups2';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import capitalizeEachFirstWord from "../../utils/CapLetters";
import FormatNumberCounter from "../../utils/FormatNumberCounter";
import SOCKET_EVENTS from "../../config/SocketEvents";

const TEAM_ACCENT_COLORS = {
  admin: "#595c8b",
  team1: "#add8e6",
  team2: "#98fb98",
  team3: "#ffffe0",
  team4: "#e6e6fa",
  team5: "#ffa07a",
  team6: "#d3d3d3",
  team7: "#ffb6c1",
  "team-spectator": "#ffb6c1",
};

const getTeamAccentColor = (teamCode) => TEAM_ACCENT_COLORS[teamCode] || "#ffffff";

// Separate MarqueeText component
const MarqueeText = ({ text, className, onClick }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [shouldMarquee, setShouldMarquee] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const textWidth = textRef.current.scrollWidth;
        setShouldMarquee(textWidth > containerWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text]);

  // Get the original style from the className to preserve alignment
  // We're assuming the className has styles that determine left/right alignment
  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  return (
    <div
      ref={containerRef}
      className={className}
      onClick={handleClick}
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        cursor: onClick ? 'pointer' : 'default',
        // Don't override any existing flex or alignment properties from className
      }}
    >
      <span
        ref={textRef}
        style={{
          display: 'inline-block',
          width: shouldMarquee ? '100%' : 'auto', // Only force width when marquee is active
        }}
      >
        {shouldMarquee ? (
          // eslint-disable-next-line jsx-a11y/no-distracting-elements
          <marquee scrollamount="3">
            {capitalizeEachFirstWord(text)}
          </marquee>
        ) : (
          capitalizeEachFirstWord(text)
        )}
      </span>
    </div>
  );
};

// Main ParticipantVideo component
const ParticipantVideo = React.memo(({
  roomId,
  classes,
  participantData,
  currentToolboxScreenshot,
  countdown,
  isParticipantOnSpectatorMode,
  isUserOnSpectatorMode,
  handleOpenReactionModal,
  reactToRoomScreenshotApiCall,
  onCountdownEnd,
  socket,
  userData,
}) => {
  const videoContainerRef = useRef(null);
  const screenshotCanvasRef = useRef(null);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  const screenshotRef = useRef(null);
  const [userReactions, setUserReactions] = useState({ like: false, dislike: false, tip: false });
  const [reactionCounts, setReactionCounts] = useState({ like: 0, dislike: 0, tip: 0 });
  const pendingReactionsRef = useRef({ like: false, dislike: false, tip: false });
  const previousScreenshotRef = useRef(null);
  const [spectatorVotes, setSpectatorVotes] = useState([]);
  const [hasVoted, setHasVoted] = useState(null);
  const [showVoteButtons, setShowVoteButtons] = useState(true);
  const [showTeamScoreSection, setShowTeamScoreSection] = useState(true);
  // New state for tracking votes per user
  const [userVotes, setUserVotes] = useState({});

  const teamRepresentingUserId = currentToolboxScreenshot?.has_team_selected ?
    currentToolboxScreenshot?.user_team?.team_representing_user_id :
    currentToolboxScreenshot?.receiver_id;

  const getScreenshotIdentifier = useCallback(() => {
    if (!currentToolboxScreenshot) return null;

    const teamIdentifier = currentToolboxScreenshot.has_team_selected
      ? currentToolboxScreenshot.user_team.team_code
      : 'individual';

    return `${currentToolboxScreenshot.id}-${teamIdentifier}`;
  }, [currentToolboxScreenshot]);

  const toggleVoteButtons = useCallback((e) => {
    e.stopPropagation();
    setShowVoteButtons(prev => !prev);
  }, []);

  const toggleTeamScoreSection = useCallback((e) => {
    e.stopPropagation();
    setShowTeamScoreSection(prev => !prev);
  }, []);

  const handleSpectatorVote = useCallback((voteType) => {
    if (!isUserOnSpectatorMode || !socket || hasVoted) return;

    // Update votes for this specific user
    setUserVotes(prev => {
      const userId = participantData.userId;
      const currentUserVotes = prev[userId] || { up: 0, down: 0 };

      return {
        ...prev,
        [userId]: {
          ...currentUserVotes,
          [voteType]: currentUserVotes[voteType] + 1
        }
      };
    });

    const voteData = {
      roomId,
      teamCode: participantData.userTeamCode,
      teamName: participantData.userTeamName,
      teamId: participantData.userTeamId,
      teamRepresentingUserId: participantData.userId,
      voterName: capitalizeEachFirstWord(userData.username),
      voteType,
      timestamp: Date.now()
    };

    socket.emit(SOCKET_EVENTS.SPECTATOR_VOTE, voteData);
    setHasVoted(voteType);

    setTimeout(() => {
      setHasVoted(null);
    }, 33000); // 33 seconds
  }, [isUserOnSpectatorMode, socket, roomId, participantData, userData, hasVoted]);

  const handleScreenshotReaction = useCallback((type) => {
    if (!currentToolboxScreenshot || !socket) return;

    const screenshotId = getScreenshotIdentifier();
    if (!screenshotId) return;

    const teamData = currentToolboxScreenshot.has_team_selected
      ? {
        teamCode: currentToolboxScreenshot.user_team.team_code,
        teamName: currentToolboxScreenshot.user_team.team_name
      }
      : null;

    setUserReactions(prev => {
      const newReactions = { ...prev };
      if (type === 'like' || type === 'dislike') {
        if (type === 'like' && prev.dislike) newReactions.dislike = false;
        if (type === 'dislike' && prev.like) newReactions.like = false;
        newReactions[type] = !prev[type];
      } else if (type === 'tip') {
        newReactions.tip = !prev.tip;
      }
      pendingReactionsRef.current = newReactions;

      socket.emit(SOCKET_EVENTS.SCREENSHOT_REACTION_UPDATE, {
        roomId,
        screenshotId,
        teamData,
        reactionType: type,
        active: newReactions[type],
      });

      return newReactions;
    });
  }, [currentToolboxScreenshot, socket, roomId, getScreenshotIdentifier]);

  const drawScreenshot = useCallback((canvasElement, screenshot) => {
    if (canvasElement && screenshot && screenshot.screenshot !== screenshotRef.current) {
      screenshotRef.current = screenshot.screenshot;
      const ctx = canvasElement.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
        ctx.drawImage(img, 0, 0, canvasElement.width, canvasElement.height);
      };
      img.onerror = (error) => {
        console.error('Error loading screenshot image:', error);
      };
      img.src = screenshot.screenshot;
    }
  }, []);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (videoContainerRef.current) {
        const { width, height } = videoContainerRef.current.getBoundingClientRect();
        setVideoSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  useEffect(() => {
    if (!socket || !currentToolboxScreenshot) return;

    const handleReactionUpdate = (data) => {
      const currentIdentifier = getScreenshotIdentifier();
      if (data.screenshotId !== currentIdentifier) return;

      setReactionCounts(prev => {
        const newCounts = { ...prev };
        if (data.active) {
          newCounts[data.reactionType]++;
          if (data.reactionType === 'like' && data.previousReaction === 'dislike') {
            newCounts.dislike = Math.max(0, newCounts.dislike - 1);
          } else if (data.reactionType === 'dislike' && data.previousReaction === 'like') {
            newCounts.like = Math.max(0, newCounts.like - 1);
          }
        } else {
          newCounts[data.reactionType] = Math.max(0, newCounts[data.reactionType] - 1);
        }
        return newCounts;
      });

      const isForOurTeam = currentToolboxScreenshot.has_team_selected &&
        data.teamData?.teamCode === currentToolboxScreenshot.user_team.team_code;

      if (isForOurTeam) {
        setUserReactions(prev => ({
          ...prev,
          [data.reactionType]: data.active
        }));
      }
    };

    socket.on(SOCKET_EVENTS.SCREENSHOT_REACTION_UPDATE, handleReactionUpdate);

    socket.emit(SOCKET_EVENTS.GET_SCREENSHOT_REACTIONS, {
      roomId,
      screenshotId: getScreenshotIdentifier(),
      teamData: currentToolboxScreenshot.has_team_selected
        ? {
          teamCode: currentToolboxScreenshot.user_team.team_code,
          teamName: currentToolboxScreenshot.user_team.team_name
        }
        : null
    });

    return () => {
      socket.off(SOCKET_EVENTS.SCREENSHOT_REACTION_UPDATE, handleReactionUpdate);
    };
  }, [socket, currentToolboxScreenshot, roomId, getScreenshotIdentifier]);

  useEffect(() => {
    if (currentToolboxScreenshot) {
      if (previousScreenshotRef.current &&
        previousScreenshotRef.current.id !== currentToolboxScreenshot.id) {
        socket.emit(SOCKET_EVENTS.SCREENSHOT_EXPIRED, {
          roomId,
          screenshotId: `${previousScreenshotRef.current.id}-${previousScreenshotRef.current.has_team_selected
            ? previousScreenshotRef.current.user_team.team_code
            : 'individual'
            }`
        });
      }
      previousScreenshotRef.current = currentToolboxScreenshot;

      setUserReactions({ like: false, dislike: false, tip: false });
      pendingReactionsRef.current = { like: false, dislike: false, tip: false };
      setReactionCounts({ like: 0, dislike: 0, tip: 0 });

      if (screenshotCanvasRef.current) {
        screenshotCanvasRef.current.width = videoSize.width;
        screenshotCanvasRef.current.height = videoSize.height;

        const shouldShowScreenshot = isUserOnSpectatorMode ||
          (!currentToolboxScreenshot.has_team_selected ||
            currentToolboxScreenshot.user_team?.team_code === participantData.userTeamCode);

        if (shouldShowScreenshot) {
          drawScreenshot(screenshotCanvasRef.current, currentToolboxScreenshot);
        } else {
          const ctx = screenshotCanvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, videoSize.width, videoSize.height);
        }
      }
    } else {
      if (screenshotCanvasRef.current) {
        const ctx = screenshotCanvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, videoSize.width, videoSize.height);
      }
    }
  }, [
    currentToolboxScreenshot,
    videoSize,
    drawScreenshot,
    isUserOnSpectatorMode,
    participantData.userTeamCode,
    socket,
    roomId
  ]);

  useEffect(() => {
    if (countdown === 0 && currentToolboxScreenshot) {
      const reactions = pendingReactionsRef.current;
      if (reactions.like) {
        reactToRoomScreenshotApiCall(roomId, {
          screenshot_owner_id: teamRepresentingUserId,
          type: 'like'
        });
      }
      if (reactions.dislike) {
        reactToRoomScreenshotApiCall(roomId, {
          screenshot_owner_id: teamRepresentingUserId,
          type: 'dislike'
        });
      }
      if (reactions.tip) {
        reactToRoomScreenshotApiCall(roomId, {
          screenshot_owner_id: teamRepresentingUserId,
          type: 'tip'
        });
      }

      socket.emit(SOCKET_EVENTS.SCREENSHOT_EXPIRED, {
        roomId,
        screenshotId: getScreenshotIdentifier()
      });

      onCountdownEnd();
    }
  }, [
    countdown,
    currentToolboxScreenshot,
    roomId,
    teamRepresentingUserId,
    reactToRoomScreenshotApiCall,
    onCountdownEnd,
    socket,
    getScreenshotIdentifier
  ]);

  useEffect(() => {
    if (!socket) return;

    const handleVoteUpdate = (voteData) => {
      if (voteData.teamCode === participantData.userTeamCode) {
        setSpectatorVotes(prev => {
          const filteredVotes = prev.filter(v =>
            v.voterName !== voteData.voterName &&
            Date.now() - v.timestamp < 5000
          );
          return [...filteredVotes, voteData];
        });

        if (voteData.voterName === capitalizeEachFirstWord(userData.username)) {
          setHasVoted(voteData.voteType);
        }
      }
    };

    socket.on(SOCKET_EVENTS.SPECTATOR_VOTE, handleVoteUpdate);
    socket.on(SOCKET_EVENTS.SHUFFLE_VIDEO_CALL_IN_ROOM, () => {
      setHasVoted(null);
      setSpectatorVotes([]);
    });

    return () => {
      socket.off(SOCKET_EVENTS.SPECTATOR_VOTE, handleVoteUpdate);
      socket.off(SOCKET_EVENTS.SHUFFLE_VIDEO_CALL_IN_ROOM);
    };
  }, [socket, participantData.userTeamCode, userData.username]);

  const ReactionButton = ({ type, icon, active, count }) => (
    <Tooltip title={`${count} ${type}${count !== 1 ? 's' : ''}`}>
      <Badge
        badgeContent={count}
        color="primary"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <IconButton
          onClick={() => handleScreenshotReaction(type)}
          size="small"
        >
          {React.cloneElement(icon, {
            style: {
              fontSize: '13px',
              color: active ? 'gold' : 'whitesmoke'
            }
          })}
        </IconButton>
      </Badge>
    </Tooltip>
  );

  const Watermark = () => {
    if (!currentToolboxScreenshot) return null;

    const userTeam = currentToolboxScreenshot?.user_team?.team_name;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '8px',
        height: '30px',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        width: 'calc(100% - 26px)',
        margin: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1px',
        }}>
          <div>{userTeam ? `Team: ${capitalizeEachFirstWord(userTeam)}` : ''}</div>
          <span>Sent by: {currentToolboxScreenshot.username}</span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ReactionButton
              type="like"
              icon={<ThumbUpIcon />}
              active={userReactions.like}
              count={reactionCounts.like}
            />
            <ReactionButton
              type="dislike"
              icon={<ThumbDownIcon />}
              active={userReactions.dislike}
              count={reactionCounts.dislike}
            />
            <ReactionButton
              type="tip"
              icon={<MonetizationOnIcon />}
              active={userReactions.tip}
              count={reactionCounts.tip}
            />
          </div>
          <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{countdown}s</div>
        </div>
      </div>
    );
  };

  // Get current user's vote counts
  const currentUserVotes = userVotes[participantData.userId] || { up: 0, down: 0 };
  const participantTeamAccentColor = getTeamAccentColor(participantData.userTeamCode);
  const participantTeamScore = Number.isFinite(Number(participantData.userTeamScore))
    ? FormatNumberCounter(Number(participantData.userTeamScore))
    : participantData.userTeamScore || 0;

  // If participant is in spectator mode, don't render video content
  if (isParticipantOnSpectatorMode) {
    return false;
  }

  return (
    <div ref={videoContainerRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          zIndex: 1200,
          background: 'rgba(0, 0, 0, 0.88)',
          color: participantTeamAccentColor,
          border: `1px solid ${participantTeamAccentColor}`,
          borderRadius: '8px',
          padding: '4px 8px',
          fontSize: 'clamp(8px, 1vw, 14px)',
          fontWeight: 700,
          lineHeight: 1.2,
          pointerEvents: 'none',
          maxWidth: 'calc(100% - 16px)',
        }}
      >
        {`${capitalizeEachFirstWord(participantData.userTeamName || 'Admin')} • ${participantTeamScore}`}
      </div>
      {participantData.videoComponent}
      <canvas
        ref={screenshotCanvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {currentToolboxScreenshot && (
        <div
          style={{
            position: 'absolute',
            bottom: '55px',
            left: 0,
            width: '100%',
            pointerEvents: 'auto',
            zIndex: 1500,
          }}
        >
          <Watermark />
        </div>
      )}

      {/* Add spectator voting UI */}
      {isUserOnSpectatorMode && (
        <>
          {showVoteButtons && (
            <div className={classes.spectatorVotingButtons}>
              <Tooltip title={`${currentUserVotes.up} Total Upvotes for ${participantData.username}`}>
                <span style={{ position: 'relative' }}>
                  <IconButton
                    onClick={() => handleSpectatorVote('up')}
                    className={`${classes.spectatorButton} ${hasVoted === 'up' ? classes.votedButton : ''}`}
                    disabled={hasVoted !== null}
                    size="small"
                  >
                    <ThumbUpIcon sx={{
                      color: hasVoted === null ? 'white' :
                        hasVoted === 'up' ? '#FFD700' : '#666'
                    }} />
                  </IconButton>
                  <span className={classes.voteCount}>{currentUserVotes.up}</span>
                </span>
              </Tooltip>

              <Tooltip title={`${currentUserVotes.down} Total Downvotes for ${participantData.username}`}>
                <span style={{ position: 'relative' }}>
                  <IconButton
                    onClick={() => handleSpectatorVote('down')}
                    className={`${classes.spectatorButton} ${hasVoted === 'down' ? classes.votedButton : ''}`}
                    disabled={hasVoted !== null}
                    size="small"
                  >
                    <ThumbDownIcon sx={{
                      color: hasVoted === null ? 'white' :
                        hasVoted === 'down' ? '#FFD700' : '#666'
                    }} />
                  </IconButton>
                  <span className={classes.voteCount}>{currentUserVotes.down}</span>
                </span>
              </Tooltip>
            </div>
          )}
        </>
      )}

      {showTeamScoreSection && (
        <>
          <div className={classes.teamScoreSection}>
            <Tooltip title={`Total team score for ${participantData.userTeamName} is ${participantData.userTeamScore.toLocaleString()}`}>
              <span style={{ position: 'relative' }}>
                <Groups2Icon sx={{
                  color: 'white'
                }} />
                <span className={classes.voteCount}>{FormatNumberCounter(participantData.userTeamScore)}</span>
              </span>
            </Tooltip>
          </div>
        </>
      )}

      {/* Display spectator votes */}
      <div className={classes.spectatorVotesContainer}>
        {spectatorVotes.map((vote) => (
          <div key={vote.timestamp} className={classes.voteContainer}>
            <span className={classes.voteIcon}>
              {vote.voteType === 'up' ? '👍' : '👎'}
            </span>
            <span className={classes.voteDescription}>
              {`${vote.voterName} ${vote.voteType === 'up' ? 'liked' : 'disliked'}`}
            </span>
          </div>
        ))}
      </div>

      <div className={classes.participantRoomIdentifier}>
        <MarqueeText
          className={classes.usernameIdentifier}
          text={participantData.username}
        />
        <div className={classes.reactionButtonContainer}>
          {!isUserOnSpectatorMode && (
            <IconButton
              aria-label="emoji reactions"
              onClick={() => handleOpenReactionModal(participantData)}
            >
              <AddReactionIcon style={{ color: "white" }} sx={{ fontSize: '25px' }} />
            </IconButton>
          )}

          {isUserOnSpectatorMode && (
            <div className={showVoteButtons ? classes.toggleVoteButtons : classes.toggleVoteButtonsHide} onClick={toggleVoteButtons}>
              <ThumbUpIcon sx={{ fontSize: '16px', color: 'white' }} />
            </div>
          )}
        </div>
        <Tooltip title={`${showTeamScoreSection ? 'Hide' : 'Show'} team score`}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <MarqueeText
              onClick={toggleTeamScoreSection}
              className={`${classes.userTeamNameIdentifier} ${participantData.userTeamCode}`}
              text={participantData.userTeamName}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  );
});

export default ParticipantVideo;